import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  AdConfigDoc,
  AdTypeConfig,
  AdTypeKey,
  PlacementsDoc,
  adTypeKeys,
  adTypeLabels,
  defaultAdTypeConfig,
  normalizeAdConfigDoc,
  normalizePlacementsDoc,
} from '../utils/adSystem';

const AdminAdsPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<Record<AdTypeKey, boolean>>({
    house: false,
    lean: false,
    sponsor: false,
  });

  const [adConfig, setAdConfig] = useState<AdConfigDoc>(normalizeAdConfigDoc(undefined));
  const [placements, setPlacements] = useState<PlacementsDoc>(normalizePlacementsDoc(undefined));

  const adTypes: AdTypeKey[] = useMemo(() => adTypeKeys, []);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const cfgSnap = await getDoc(doc(db, 'admin', 'adConfig'));
        setAdConfig(normalizeAdConfigDoc(cfgSnap.exists() ? cfgSnap.data() : undefined));
        const plcSnap = await getDoc(doc(db, 'admin', 'adPlacements'));
        setPlacements(normalizePlacementsDoc(plcSnap.exists() ? plcSnap.data() : undefined));
      } catch (e) {
        console.error('AdminAds: load error', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin, navigate]);

  const cfgFor = (key: AdTypeKey): AdTypeConfig => ({ ...defaultAdTypeConfig(), ...(adConfig?.[key] || {}) });

  const onFieldChange = (key: AdTypeKey, field: keyof AdTypeConfig, value: any) => {
    setAdConfig(prev => ({
      ...(prev || {}),
      [key]: { ...(prev?.[key] || {}), [field]: value }
    }));
  };

  const uploadImage = async (key: AdTypeKey, file: File) => {
    try {
      setUploading(prev => ({ ...prev, [key]: true }));
      const path = `ads/${key}/${Date.now()}-${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type });
      const url = await getDownloadURL(storageRef);
      setAdConfig(prev => ({
        ...(prev || {}),
        [key]: { ...(prev?.[key] || {}), imageUrl: url }
      }));
    } catch (e) {
      console.error('Upload error', e);
      alert('Image upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'admin', 'adConfig'), adConfig, { merge: true });
      await setDoc(doc(db, 'admin', 'adPlacements'), placements, { merge: true });
      alert('Saved successfully');
    } catch (e) {
      console.error('Save error', e);
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return <div className="p-6">Access Denied</div>;

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin • Ads Manager</h1>
            <p className="text-gray-600 text-sm">Manage house promos, sponsor creatives, CTAs, and placements. Images are stored in Firebase Storage.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => navigate('/admin')} className="px-4 py-2 rounded bg-gray-200 text-gray-800">Back</button>
            <button onClick={saveAll} disabled={saving} className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Creatives */}
            <section className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Creatives</h2>
                <span className="text-xs text-gray-500">Fields: title, description, image/video, CTA text/href, gradient color</span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {adTypes.map((key) => {
                  const cfg = cfgFor(key);
                  return (
                    <div key={key} className="rounded-lg border border-gray-200 p-4">
                      <h3 className="font-medium text-gray-900 mb-3">{adTypeLabels[key]}</h3>
                      <div className="space-y-3 text-sm">
                        <label className="block">
                          <span className="text-gray-700">Title</span>
                          <input className="mt-1 w-full border rounded px-3 py-2" value={cfg.title} onChange={e => onFieldChange(key, 'title', e.target.value)} />
                        </label>
                        <label className="block">
                          <span className="text-gray-700">Description</span>
                          <textarea className="mt-1 w-full border rounded px-3 py-2" rows={3} value={cfg.description} onChange={e => onFieldChange(key, 'description', e.target.value)} />
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <label className="block">
                            <span className="text-gray-700">CTA Text</span>
                            <input className="mt-1 w-full border rounded px-3 py-2" value={cfg.ctaText} onChange={e => onFieldChange(key, 'ctaText', e.target.value)} />
                          </label>
                          <label className="block">
                            <span className="text-gray-700">CTA Href</span>
                            <input className="mt-1 w-full border rounded px-3 py-2" value={cfg.ctaHref} onChange={e => onFieldChange(key, 'ctaHref', e.target.value)} />
                          </label>
                        </div>
                        <label className="block">
                          <span className="text-gray-700">Gradient Color</span>
                          <input className="mt-1 w-full border rounded px-3 py-2" placeholder="from-purple-600 to-blue-600" value={cfg.color} onChange={e => onFieldChange(key, 'color', e.target.value)} />
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <label className="block">
                            <span className="text-gray-700">YouTube Video ID (optional)</span>
                            <input className="mt-1 w-full border rounded px-3 py-2" value={cfg.videoId} onChange={e => onFieldChange(key, 'videoId', e.target.value)} />
                          </label>
                          <label className="block">
                            <span className="text-gray-700">Image Alt</span>
                            <input className="mt-1 w-full border rounded px-3 py-2" value={cfg.imageAlt} onChange={e => onFieldChange(key, 'imageAlt', e.target.value)} />
                          </label>
                        </div>

                        {/* Preview */}
                        {cfg.imageUrl ? (
                          <div className="mt-2">
                            <img src={cfg.imageUrl} alt={cfg.imageAlt || cfg.title || key} className="w-full h-32 object-contain bg-black rounded" />
                            <div className="text-xs text-gray-500 truncate mt-1">{cfg.imageUrl}</div>
                          </div>
                        ) : (
                          <div className="mt-2 border border-dashed rounded p-4 text-center text-gray-500">No image uploaded</div>
                        )}

                        <div className="flex items-center gap-3 mt-2">
                          <label className="inline-block">
                            <span className="sr-only">Upload</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) uploadImage(key, f);
                              }}
                            />
                          </label>
                          {uploading[key] && <span className="text-xs text-gray-600">Uploading…</span>}
                          {cfg.imageUrl && (
                            <button className="text-xs text-red-600" onClick={() => onFieldChange(key, 'imageUrl', '')}>Remove Image</button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Placements */}
            <section className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Placements</h2>
                <span className="text-xs text-gray-500">Choose which ad types show and how often</span>
              </div>

              {(['viewerSplash', 'hostBreak'] as (keyof PlacementsDoc)[]).map((placeKey) => {
                const plc = placements[placeKey] || { enabledTypes: adTypes, rotationSeconds: 15 };
                return (
                  <div key={placeKey} className="border rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900">{placeKey === 'viewerSplash' ? 'Viewer Splash' : 'Host Break'}</h3>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-700">Rotation (sec)</span>
                        <input
                          type="number"
                          min={5}
                          value={plc.rotationSeconds}
                          onChange={(e) => setPlacements(prev => ({
                            ...(prev || {}),
                            [placeKey]: { ...plc, rotationSeconds: Number(e.target.value) || 15 }
                          }))}
                          className="w-24 border rounded px-2 py-1"
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mt-3">
                      {adTypes.map((t) => {
                        const checked = plc.enabledTypes.includes(t);
                        return (
                          <label key={t} className={`px-3 py-1 rounded-full border cursor-pointer text-sm ${checked ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-gray-50 border-gray-300 text-gray-700'}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const next = new Set(plc.enabledTypes);
                                if (e.target.checked) next.add(t); else next.delete(t);
                                setPlacements(prev => ({
                                  ...(prev || {}),
                                  [placeKey]: { ...plc, enabledTypes: Array.from(next) as AdTypeKey[] }
                                }));
                              }}
                              className="mr-2"
                            />
                            {adTypeLabels[t]}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAdsPage;
